// v3.99c.71 — VoteControl
// Upvote / downvote arrows with score display. Logged-out users see the
// score and the buttons but tapping prompts them to sign up (via the
// passed `onAuthRequired` callback).
import React, { useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { api } from "../../lib/apiClient";

export default function VoteControl({ itemId, initialVote = 0, initialScore = 0,
                                      isAuthed = false, onAuthRequired, testid = "vote",
                                      vertical = true }) {
  const [vote, setVote] = useState(initialVote);
  const [score, setScore] = useState(initialScore);
  const [busy, setBusy] = useState(false);

  const cast = async (next) => {
    if (!isAuthed) { onAuthRequired?.(); return; }
    if (busy) return;
    const effective = vote === next ? 0 : next;
    // Optimistic: compute new score immediately.
    const delta = effective - vote;
    setVote(effective);
    setScore((s) => s + delta);
    setBusy(true);
    try {
      const r = await api.post(`/feed/${itemId}/vote`, { vote: effective });
      setScore(r.data.score);
      setVote(r.data.my_vote);
    } catch {
      // Roll back optimistic state on failure.
      setVote(vote);
      setScore((s) => s - delta);
    } finally {
      setBusy(false);
    }
  };

  const containerCls = vertical
    ? "inline-flex flex-col items-center gap-0.5"
    : "inline-flex items-center gap-1";

  return (
    <div className={containerCls} data-testid={testid}>
      <button
        type="button"
        onClick={() => cast(1)}
        disabled={busy}
        aria-label="Upvote"
        aria-pressed={vote === 1}
        className="w-7 h-7 inline-flex items-center justify-center border transition-colors"
        style={{
          borderColor: vote === 1 ? "var(--gold)" : "var(--border)",
          color: vote === 1 ? "var(--gold)" : "var(--text-2)",
          background: vote === 1 ? "rgba(212,175,55,0.10)" : "transparent",
          borderRadius: 2,
        }}
        data-testid={`${testid}-up`}
      >
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <span
        className="font-mono text-xs tabular-nums min-w-[1.5em] text-center"
        style={{ color: vote !== 0 ? "var(--gold)" : "var(--text-2)" }}
        data-testid={`${testid}-score`}
      >
        {score}
      </span>
      <button
        type="button"
        onClick={() => cast(-1)}
        disabled={busy}
        aria-label="Downvote"
        aria-pressed={vote === -1}
        className="w-7 h-7 inline-flex items-center justify-center border transition-colors"
        style={{
          borderColor: vote === -1 ? "#FF7A6A" : "var(--border)",
          color: vote === -1 ? "#FF7A6A" : "var(--text-2)",
          background: vote === -1 ? "rgba(255,122,106,0.10)" : "transparent",
          borderRadius: 2,
        }}
        data-testid={`${testid}-down`}
      >
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
